import React, { useState } from "react";
import { env } from "../../env/env";

export function rendererThumbUrl(id: string, height: number = env.rendererImageHeight): string {
  return `${env.rendererPath}/design/thumbnail/${id}?height=${height}`;
}

export interface RendererThumbProps {
  /** Design id — used to compute the standard renderer thumbnail URL when `src` isn't provided. */
  id?: string;
  /** Explicit src override (e.g. uploaded PDF thumbnails on a non-renderer path). Takes precedence over `id`. */
  src?: string;
  height?: number;
  alt?: string;
  className?: string;
  loading?: "lazy" | "eager";
  imgRef?: React.RefObject<HTMLImageElement>;
  /** React node rendered when no id/src is available or onError fires. */
  fallback?: React.ReactNode;
  /** When set, on error swap to an <img src={fallbackSrc}> instead of unmounting. */
  fallbackSrc?: string;
  /** Style applied to the fallback <img> (e.g. shrink placeholder). */
  fallbackStyle?: React.CSSProperties;
}

export default function RendererThumb({
  id,
  src,
  height,
  alt = "",
  className,
  loading,
  imgRef,
  fallback,
  fallbackSrc,
  fallbackStyle,
}: RendererThumbProps) {
  const [failed, setFailed] = useState(false);
  const resolved = src || (id ? rendererThumbUrl(id, height ?? env.rendererImageHeight) : "");

  if (!resolved || failed) {
    if (fallbackSrc) {
      return (
        <img
          ref={imgRef}
          src={fallbackSrc}
          alt={alt}
          className={className}
          style={fallbackStyle}
        />
      );
    }
    return fallback ? <>{fallback}</> : null;
  }

  return (
    <img
      ref={imgRef}
      loading={loading}
      alt={alt}
      className={className}
      src={resolved}
      onError={() => setFailed(true)}
    />
  );
}
